import React from 'react';
import { Circle, Box, Cylinder } from '@react-three/drei';
import * as THREE from 'three';

export const CricketField: React.FC = () => {
  return (
    <group>
      {/* Ground plane */}
      <Circle
        args={[75]} // 75m radius for boundary
        rotation-x={-Math.PI / 2}
        position={[0, 0, 0]}
        receiveShadow
      >
        <meshLambertMaterial color="#4ade80" />
      </Circle>

      {/* Boundary rope */}
      <Circle
        args={[75, 64]}
        rotation-x={-Math.PI / 2}
        position={[0, 0.02, 0]}
      >
        <meshBasicMaterial color="#ffffff" side={THREE.DoubleSide} />
        <ringGeometry args={[74.5, 75]} />
      </Circle>

      {/* Cricket pitch */}
      <Box
        args={[2, 0.01, 20.12]} // 20.12m length (22 yards)
        position={[0, 0.01, 0]}
        receiveShadow
      >
        <meshLambertMaterial color="#d2b48c" />
      </Box>

      {/* Wickets - Bowler's end */}
      <group position={[0, 0, 10.06]}>
        {/* Stumps */}
        {[-0.23, 0, 0.23].map((x, i) => (
          <Cylinder
            key={`bowler-stump-${i}`}
            args={[0.015, 0.015, 0.71]}
            position={[x, 0.355, 0]}
          >
            <meshLambertMaterial color="#8b4513" />
          </Cylinder>
        ))}
        {/* Bails */}
        <Cylinder
          args={[0.005, 0.005, 0.46]}
          position={[0, 0.71, 0]}
          rotation-z={Math.PI / 2}
        >
          <meshLambertMaterial color="#8b4513" />
        </Cylinder>
      </group>

      {/* Wickets - Batsman's end */}
      <group position={[0, 0, -10.06]}>
        {/* Stumps */}
        {[-0.23, 0, 0.23].map((x, i) => (
          <Cylinder
            key={`batsman-stump-${i}`}
            args={[0.015, 0.015, 0.71]}
            position={[x, 0.355, 0]}
          >
            <meshLambertMaterial color="#8b4513" />
          </Cylinder>
        ))}
        {/* Bails */}
        <Cylinder
          args={[0.005, 0.005, 0.46]}
          position={[0, 0.71, 0]}
          rotation-z={Math.PI / 2}
        >
          <meshLambertMaterial color="#8b4513" />
        </Cylinder>
      </group>

      {/* Creases */}
      {/* Bowling crease */}
      <Box
        args={[2.64, 0.005, 0.1]}
        position={[0, 0.02, 10.06]}
      >
        <meshBasicMaterial color="#ffffff" />
      </Box>
      
      {/* Batting crease */}
      <Box
        args={[1.22, 0.005, 0.1]}
        position={[0, 0.02, -8.84]}
      >
        <meshBasicMaterial color="#ffffff" />
      </Box>

      {/* Return creases */}
      <Box
        args={[0.1, 0.005, 2.44]}
        position={[1.32, 0.02, 8.84]}
      >
        <meshBasicMaterial color="#ffffff" />
      </Box>
      <Box
        args={[0.1, 0.005, 2.44]}
        position={[-1.32, 0.02, 8.84]}
      >
        <meshBasicMaterial color="#ffffff" />
      </Box>

      {/* Popping crease */}
      <Box
        args={[3.66, 0.005, 0.1]}
        position={[0, 0.02, -8.84]}
      >
        <meshBasicMaterial color="#ffffff" />
      </Box>

      {/* 30-yard circle */}
      <Circle
        args={[27.43, 64]} // 30 yards = 27.43m
        rotation-x={-Math.PI / 2}
        position={[0, 0.015, 0]}
      >
        <meshBasicMaterial color="#ffffff" transparent opacity={0.3} side={THREE.DoubleSide} />
        <ringGeometry args={[27.2, 27.43]} />
      </Circle>

      {/* Field markers/cones at key positions */}
      {/* Slip positions */}
      {[
        [3, 0, 8], [4, 0, 7], [5, 0, 6],
        [-3, 0, 8], [-4, 0, 7], [-5, 0, 6]
      ].map((pos, i) => (
        <Cylinder
          key={`cone-${i}`}
          args={[0.1, 0.2, 0.5]}
          position={pos as [number, number, number]}
        >
          <meshLambertMaterial color="#ff6b35" />
        </Cylinder>
      ))}

      {/* Square leg and point markers */}
      <Cylinder
        args={[0.1, 0.2, 0.5]}
        position={[20, 0, 0]}
      >
        <meshLambertMaterial color="#ff6b35" />
      </Cylinder>
      <Cylinder
        args={[0.1, 0.2, 0.5]}
        position={[-20, 0, 0]}
      >
        <meshLambertMaterial color="#ff6b35" />
      </Cylinder>

      {/* Long-on and long-off markers */}
      <Cylinder
        args={[0.1, 0.2, 0.5]}
        position={[0, 0, 40]}
      >
        <meshLambertMaterial color="#ff6b35" />
      </Cylinder>
      <Cylinder
        args={[0.1, 0.2, 0.5]}
        position={[0, 0, -40]}
      >
        <meshLambertMaterial color="#ff6b35" />
      </Cylinder>

      {/* Mid-wicket and cover markers */}
      <Cylinder
        args={[0.1, 0.2, 0.5]}
        position={[15, 0, 15]}
      >
        <meshLambertMaterial color="#ff6b35" />
      </Cylinder>
      <Cylinder
        args={[0.1, 0.2, 0.5]}
        position={[-15, 0, 15]}
      >
        <meshLambertMaterial color="#ff6b35" />
      </Cylinder>

      {/* Batsman's position marker */}
      <Circle
        args={[0.5]}
        rotation-x={-Math.PI / 2}
        position={[0, 0.02, -8]}
      >
        <meshBasicMaterial color="#3b82f6" transparent opacity={0.7} />
      </Circle>
    </group>
  );
};